/**
 * YesNoToggle.js
 *
 * The Yes/No pill pair used by the CYP Clinical Interview's clinician
 * panels (structured risk assessment, treatment contraindication screening).
 * Moved out of CypAdhdStructuredRiskPanel.js unchanged so the two panels
 * share one control rather than two copies that could drift.
 *
 * Vocabulary is the Adult Autism HistoryRiskAssessmentPanel's: two
 * half-rounded buttons that read as one control, green-200 for a recorded
 * "Yes" and red-200 for a recorded "No" with a heavier grey border. Colour
 * never carries the meaning alone: the label inside each half does, and
 * aria-pressed exposes the state to assistive tech.
 *
 * Props:
 *   value         true | false | null  (null = not yet answered)
 *   onChange      called with the clicked value (true or false). Deciding
 *                 whether a second click on the lit half clears the answer
 *                 is the parent's job, so each panel can keep its own rule.
 *   disabled      greys both halves and blocks clicks (read-only viewers).
 *   categoryLabel the row's label, used for the group's accessible name
 *                 ("<label> present").
 */
import React from 'react';

export default function YesNoToggle({ value, onChange, disabled, categoryLabel }) {
  const base = 'py-1 px-3 border font-semibold text-sm transition-all duration-200';
  return (
    <div
      className="inline-flex"
      role="group"
      aria-label={`${categoryLabel} present`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(true)}
        aria-pressed={value === true}
        className={`${base} rounded-l-full ${
          value === true
            ? 'bg-green-200 border-gray-600 border-2'
            : 'bg-white border-gray-400'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        Yes
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(false)}
        aria-pressed={value === false}
        className={`${base} rounded-r-full ${
          value === false
            ? 'bg-red-200 border-gray-600 border-2'
            : 'bg-white border-gray-400'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        No
      </button>
    </div>
  );
}
